import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { skel } from "./FuturesTradeSkeletons";

function parseRate(raw) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function formatRatePct(rate, decimals = 5) {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(decimals)}%`;
}

function formatCapFloorPct(rate) {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(4)}%`;
}

function formatAnnualized(rate, intervalHours) {
  if (rate == null) return "—";
  const h = Math.max(1, Number(intervalHours) || 8);
  const annualized = rate * (24 / h) * 365 * 100;
  return `${annualized.toFixed(2)}%`;
}

function parseNextFundingMs(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1e12 ? n * 1000 : n;
}

function formatCountdown(msLeft) {
  if (msLeft == null) return "--:--:--";
  const total = Math.max(0, Math.floor(msLeft / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((x) => String(x).padStart(2, "0")).join(":");
}

/**
 * Funding rate + countdown in the futures ticker bar, with hover/click details popup.
 * Popup is portaled to document.body so overflow:hidden parents cannot clip it.
 */
export default function FuturesFundingTicker({ contract, ready = true }) {
  const intervalHours = Math.max(1, Number(contract?.funding_interval_hours) || 8);
  const rate = parseRate(contract?.funding_rate);
  const cap = parseRate(contract?.funding_rate_cap);
  const floor = parseRate(contract?.funding_rate_floor);
  const nextMs = parseNextFundingMs(contract?.next_funding_time);

  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 360 });
  const rootRef = useRef(null);
  const popupRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!ready) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ready]);

  const updatePos = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw <= 991;
    const width = isMobile ? Math.max(0, vw - 16) : Math.min(360, vw - 16);
    let left = isMobile ? 8 : r.left;
    if (!isMobile && left + width > vw - 8) {
      left = Math.max(8, vw - width - 8);
    }
    let top = r.bottom + 8;
    const approxPopupH = isMobile ? 260 : 220;
    if (top + approxPopupH > vh - 8) {
      top = Math.max(8, r.top - approxPopupH - 8);
    }
    setPos({ top, left, width });
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openPopup = useCallback(() => {
    clearCloseTimer();
    updatePos();
    setOpen(true);
  }, [clearCloseTimer, updatePos]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updatePos();
    const onDocDown = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, updatePos]);

  const countdown = formatCountdown(nextMs != null ? nextMs - now : null);
  const ratePct = formatRatePct(rate);
  const longsPay = rate == null ? true : rate >= 0;

  const popup =
    ready && open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={popupRef}
            className="agcx-ft-funding-popup agcx-ft-funding-popup--portal"
            role="tooltip"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            onMouseEnter={openPopup}
            onMouseLeave={scheduleClose}
          >
            <p className="agcx-ft-funding-popup__intro">
              The payment rate exchanged between the long and short positions for the next funding. If
              the funding rate is positive, longs pay shorts. If negative, shorts pay longs.
            </p>

            <div className="agcx-ft-funding-popup__rows">
              <div className="agcx-ft-funding-popup__row">
                <span className="agcx-ft-funding-popup__key">Interval / Direction</span>
                <span className="agcx-ft-funding-popup__val">
                  {intervalHours}h /{" "}
                  {longsPay ? (
                    <>
                      Long Pays <span className="agcx-ft-funding-popup__payee">Short</span>
                    </>
                  ) : (
                    <>
                      Short Pays <span className="agcx-ft-funding-popup__payee">Long</span>
                    </>
                  )}
                </span>
              </div>
              <div className="agcx-ft-funding-popup__row">
                <span className="agcx-ft-funding-popup__key">Current Rate / Annualized</span>
                <span className="agcx-ft-funding-popup__val">
                  {ratePct} / {formatAnnualized(rate, intervalHours)}
                </span>
              </div>
              <div className="agcx-ft-funding-popup__row">
                <span className="agcx-ft-funding-popup__key">Funding Cap / Floor</span>
                <span className="agcx-ft-funding-popup__val">
                  {formatCapFloorPct(cap)} / {formatCapFloorPct(floor)}
                </span>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={rootRef}
      className="headline_leftItem__7BFYq agcx-ft-funding-ticker"
      onMouseEnter={ready ? openPopup : undefined}
      onMouseLeave={ready ? scheduleClose : undefined}
    >
      <button
        type="button"
        className="agcx-ft-funding-ticker__trigger"
        aria-expanded={open}
        aria-haspopup="true"
        disabled={!ready}
        onClick={(e) => {
          if (!ready) return;
          e.preventDefault();
          e.stopPropagation();
          if (open) setOpen(false);
          else openPopup();
        }}
      >
        <div className="headline_withBorder__a6ZD2 agcx-ft-funding-ticker__label">
          <span className="agcx-ft-funding-ticker__label-full">Funding ({intervalHours}h) / Countdown</span>
          <span className="agcx-ft-funding-ticker__label-short">Funding ({intervalHours}h) / Countdown</span>
        </div>
        <div className="headline_title__x1csO font-weight-boldd agcx-ft-funding-ticker__value">
          {ready ? (
            <>
              <span className="agcx-ft-funding-ticker__rate">{ratePct}</span>
              <span className="agcx-ft-funding-ticker__sep"> / </span>
              <span className="agcx-ft-funding-ticker__countdown">{countdown}</span>
            </>
          ) : (
            skel("lg")
          )}
        </div>
      </button>
      {popup}
    </div>
  );
}
